import { useApprovalCategoryById } from "@/queries/approval/use-approval-categories.query.ts";
import { useApprovalPoliciesSort } from "@/queries/approval/use-approval-policies.query.ts";
import { ApiApprovalPolicy } from "@/types/approvals/approval-policy.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApprovalPoliciesSortModalProps extends ModalProps {
  categoryId: number;
}

export function ApprovalPoliciesSortModal({
  categoryId,
  ...props
}: ApprovalPoliciesSortModalProps) {
  const { data: category, isPending } = useApprovalCategoryById(categoryId);
  const [sortedPolicies, setSortedPolicies] = useState<ApiApprovalPolicy[]>(
    category?.policies ?? []
  );
  const [syncedCategory, setSyncedCategory] = useState(category);
  const approvalPoliciesSort = useApprovalPoliciesSort(categoryId);
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  if (category !== syncedCategory) {
    setSyncedCategory(category);
    if (category) setSortedPolicies(category.policies);
  }

  const saveOrder = async () => {
    await approvalPoliciesSort.mutateAsync(sortedPolicies.map((policy) => policy.id));
    close(id);
  };

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || destination.index === source.index) return;

    const items = Array.from(sortedPolicies);
    const [moved] = items.splice(source.index, 1);

    items.splice(destination.index, 0, moved);
    setSortedPolicies(items);
  };

  return (
    <Modal
      title={t("common:reordering")}
      {...props}
      withCloser
      actions={
        <Stack>
          <Button size={"lg"} onClick={saveOrder} isLoading={approvalPoliciesSort.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack direction={"vertical"} gap={"md"}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Stack key={index} direction={"horizontal"} gap={"md"} items={"center"}>
              <Skeleton className={"h-4 w-48"} />
            </Stack>
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"approval-policies-list"} direction={"vertical"}>
            {(provided) => (
              <div
                className={"w-full flex flex-col overflow-y-auto"}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sortedPolicies.map((policy, index) => (
                  <Draggable key={policy.id} draggableId={policy.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        className={"flex items-center gap-4 bg-light py-1 min-h-16"}
                      >
                        <div
                          className={"flex items-center justify-center cursor-grab text-secondary"}
                        >
                          <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                        </div>
                        <Text>{policy.title}</Text>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
